import {
  CatmullRomCurve3,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
} from 'three';
import { createRandom } from '../../../utils/random.js';
import { MEDIAN_OPENINGS } from './route.js';
import { Parts } from './parts.js';
import { BUS_DOOR, BUS_SEATS } from './rides.js';

/**
 * Street 60's minibuses.
 *
 * The reference is the Rosa-class midibus that runs every Sudanese city line:
 * a 7 m two-tone box, cream over blue, a long luggage rack on the roof, a
 * split windscreen and one wide sliding door on the right. Passengers climb in
 * through that door and sit two abreast; the kumsary rides in the open doorway
 * with one hand on the frame and the other out over the road, calling the
 * street in.
 *
 * A bus is a scripted vehicle on a closed circuit between two median openings:
 * up the northbound carriageway at the kerb, a U-turn through the gap, back
 * down the southbound side, and another U-turn. It never joins the fuel queue,
 * so it is exempt from the queue-rage system, but it carries six people instead
 * of a raksha's two.
 *
 * Local axes match the rest of the traffic: the nose points down -Z, the
 * vehicle's right is +X, and the origin sits on the road between the wheels.
 */

/* ------------------------------------------------------------------ */
/* Dimensions                                                          */
/* ------------------------------------------------------------------ */

const BODY_W = 2.0;
const HALF_W = BODY_W / 2;
const HALF_L = 3.5;
const WALL = 0.12;

const SKIRT_BOTTOM = 0.46;
const BELT = 1.08;
const WINDOW_BOTTOM = 1.32;
const WINDOW_TOP = 2.04;
const WINDOW_TOP_BAND = 2.22;
const ROOF_TOP = 2.42;

const FLOOR_TOP = 0.7;
const WHEEL_R = 0.38;
const AXLES = [-2.32, 1.34];

/** The doorway, cut through the right-hand side just behind the front axle,
 *  where a Rosa's sliding door actually sits. Everything on that side is built
 *  around these two values. */
const DOOR_Z0 = -1.56;
const DOOR_Z1 = -0.34;

/** Side windows as [front z, rear z]. The second slot is the doorway on the
 *  right-hand side; both sides share the same pillar positions so the window
 *  rhythm reads through the bus. */
const WINDOWS = [
  [-2.92, -1.68],
  [-1.56, -0.34],
  [0.02, 1.26],
  [1.38, 2.62],
  [2.74, 3.3],
];
const DOOR_SLOT = 1;

/** Half-width of the wheel arch opening, and the recess behind it. */
const ARCH_HALF = 0.48;

const COLORS = {
  cream: 0xd9cdb4,
  creamShade: 0xc4b79c,
  blue: 0x2c6a9e,
  blueDark: 0x215075,
  maroon: 0x843c43,
  rubber: 0x141618,
  hub: 0x6d7370,
  steel: 0x8e9492,
  chrome: 0xa8afac,
  interior: 0x3c3832,
  seat: 0x6f3a2c,
  dash: 0x211e1b,
  rust: 0x8a5c3e,
  rustDark: 0x64422c,
  glass: 0x7d9698,
  skin: 0x5d3f2b,
  shirt: 0xe2ddd0,
  trousers: 0x2c3646,
  cap: 0x1c2836,
};

/** Bounding box used by the collision, camera and bump code. */
export const BUS_DIMENSIONS = {
  width: BODY_W + 0.02,
  height: 2.62,
  length: 7.0,
  wheelZ: 3.5,
  lightZ: 3.6,
};

/**
 * Bus lines, as index pairs into `MEDIAN_OPENINGS`. Each pair is the stretch
 * between two gaps in the median, so the U-turn at either end always happens in
 * a real opening rather than on a solid divider. The pairs share no endpoint,
 * which keeps two buses from turning through the same gap at once.
 */
const BUS_SPANS = [
  [0, 3], // 330 m  -> 1580 m
  [2, 6], // 1190 m -> 2560 m
  [4, 8], // 1840 m -> 3640 m
  [7, 11], // 3100 m -> 5260 m
];

/** Outer lane, near the kerb. Blended inboard around the fuel queues. */
const BUS_LANE = 7.4;
const QUEUE_LANE = 4.4;
const LANE_BLEND = 70;

const BUS_DEFAULTS = {
  passengerCapacity: 6,
  pickupRadius: 11,
  pickupStopTime: 5,
  minRideDistance: 600,
  dropDelay: 1.6,
  respawnDelay: 30,
  topSpeed: 10.5,
  minSpeed: 4.5,
};

/* ------------------------------------------------------------------ */
/* Visuals                                                             */
/* ------------------------------------------------------------------ */

/** One cloned group per bus, plus the kumsary's waving arm. */
export function buildBusVisuals(level, records) {
  const buses = records.filter((record) => record.type === 'bus');
  if (buses.length === 0) return { update() {} };

  const template = buildBusTemplate();
  const items = buses.map((record) => {
    const group = template.clone(true);
    group.name = `bus-${record.index}`;
    group.position.set(record.x, record.y ?? 0.02, record.z);
    group.rotation.y = record.yaw;
    level.root.add(group);
    record.visual = group;
    return { record, group, arm: group.getObjectByName('bus-kumsary-arm') };
  });

  return {
    update(elapsed = 0) {
      for (const { record, group, arm } of items) {
        group.visible = record.visible !== false;
        group.position.set(record.x, record.y ?? 0.02, record.z);
        group.rotation.y = record.yaw;
        // The kumsary never stops working the street: he leans out of the door
        // and swings his free arm from the moment the bus leaves the stop.
        if (arm) {
          arm.rotation.z = 1.75 + Math.sin(elapsed * 3.4 + record.index * 1.9) * 0.34;
          arm.rotation.y = Math.sin(elapsed * 1.7 + record.index) * 0.14;
        }
      }
    },
  };
}

function buildBusTemplate() {
  const group = new Group();
  const body = new Parts();
  const glass = new Parts();
  const lamps = new Parts();
  const random = createRandom(7717);

  const sideWall = (side, y0, y1, z0, z1, color) => {
    body.box(
      WALL,
      y1 - y0,
      z1 - z0,
      color,
      side * (HALF_W - WALL / 2),
      (y0 + y1) / 2,
      (z0 + z1) / 2,
    );
  };

  /* ---- Running gear -------------------------------------------------- */

  for (const axle of AXLES) {
    for (const side of [-1, 1]) {
      const x = side * 0.9;
      body.cyl(WHEEL_R, WHEEL_R, 0.26, 14, COLORS.rubber, x, WHEEL_R, axle, { z: Math.PI / 2 });
      body.cyl(0.21, 0.21, 0.28, 12, COLORS.hub, x, WHEEL_R, axle, { z: Math.PI / 2 });
      body.cyl(0.075, 0.075, 0.3, 8, COLORS.steel, x, WHEEL_R, axle, { z: Math.PI / 2 });
    }
  }
  // Chassis rail, visible in the shadow under the skirt.
  body.box(1.86, 0.2, 6.8, COLORS.interior, 0, 0.38, 0);

  /* ---- Lower body: blue skirt, cut by the wheel arches ----------------- */

  // The skirt is built in runs so each wheel sits in a real opening rather than
  // in front of a painted panel.
  const skirtRuns = (side) => {
    const cuts = AXLES.map((axle) => [axle - ARCH_HALF, axle + ARCH_HALF]);
    if (side > 0) cuts.push([DOOR_Z0, DOOR_Z1]);
    cuts.sort((a, b) => a[0] - b[0]);
    const runs = [];
    let cursor = -HALF_L;
    for (const [start, end] of cuts) {
      if (start > cursor + 0.01) runs.push([cursor, start]);
      cursor = Math.max(cursor, end);
    }
    if (cursor < HALF_L - 0.01) runs.push([cursor, HALF_L]);
    return runs;
  };

  for (const side of [-1, 1]) {
    for (const [z0, z1] of skirtRuns(side)) {
      sideWall(side, SKIRT_BOTTOM, BELT, z0, z1, COLORS.blue);
    }
    // Dark recess behind each arch, so the opening reads as depth. The arch is
    // only as tall as the tyre it wraps; above that the skirt closes again.
    for (const axle of AXLES) {
      body.box(0.04, 0.4, ARCH_HALF * 2, COLORS.interior, side * 0.86, SKIRT_BOTTOM + 0.2, axle);
      body.box(WALL, 0.22, ARCH_HALF * 2, COLORS.blue, side * (HALF_W - WALL / 2), SKIRT_BOTTOM + 0.51, axle);
      body.box(0.03, 0.05, ARCH_HALF * 2 + 0.04, COLORS.blueDark, side * (HALF_W - 0.005), SKIRT_BOTTOM + 0.395, axle);
    }
  }
  body.box(BODY_W - WALL, BELT - SKIRT_BOTTOM, WALL, COLORS.blue, 0, (SKIRT_BOTTOM + BELT) / 2, -HALF_L + WALL / 2);
  body.box(BODY_W - WALL, BELT - SKIRT_BOTTOM, WALL, COLORS.blue, 0, (SKIRT_BOTTOM + BELT) / 2, HALF_L - WALL / 2);

  /* ---- Cabin sides ---------------------------------------------------- */

  for (const side of [-1, 1]) {
    // Cream band under the windows, interrupted by the doorway.
    if (side > 0) {
      sideWall(1, BELT, WINDOW_BOTTOM, -HALF_L, DOOR_Z0, COLORS.cream);
      sideWall(1, BELT, WINDOW_BOTTOM, DOOR_Z1, HALF_L, COLORS.cream);
    } else {
      sideWall(-1, BELT, WINDOW_BOTTOM, -HALF_L, HALF_L, COLORS.cream);
    }

    // Header band and the maroon pinstripe above the windows.
    sideWall(side, WINDOW_TOP, WINDOW_TOP_BAND, -HALF_L, HALF_L, COLORS.cream);
    sideWall(side, WINDOW_TOP_BAND, WINDOW_TOP_BAND + 0.05, -HALF_L, HALF_L, COLORS.maroon);

    // Front and rear corner panels.
    sideWall(side, WINDOW_BOTTOM, WINDOW_TOP, -HALF_L, WINDOWS[0][0], COLORS.cream);
    sideWall(side, WINDOW_BOTTOM, WINDOW_TOP, WINDOWS[WINDOWS.length - 1][1], HALF_L, COLORS.cream);

    // Pillars between the windows.
    for (let i = 0; i < WINDOWS.length - 1; i += 1) {
      sideWall(side, WINDOW_BOTTOM, WINDOW_TOP, WINDOWS[i][1], WINDOWS[i + 1][0], COLORS.cream);
    }

    // Glass. The right-hand side skips the doorway.
    for (let i = 0; i < WINDOWS.length; i += 1) {
      if (side > 0 && i === DOOR_SLOT) continue;
      const [z0, z1] = WINDOWS[i];
      glass.box(0.03, WINDOW_TOP - WINDOW_BOTTOM - 0.04, z1 - z0 - 0.04, COLORS.glass, side * 0.955, (WINDOW_BOTTOM + WINDOW_TOP) / 2, (z0 + z1) / 2);
    }
  }

  /* ---- Front ---------------------------------------------------------- */

  // Panel under the screen, then the split windscreen and the header above it.
  body.box(BODY_W - WALL, 0.26, WALL, COLORS.cream, 0, 1.21, -HALF_L + WALL / 2);
  glass.box(0.85, 0.68, 0.03, COLORS.glass, -0.47, 1.69, -HALF_L + 0.06);
  glass.box(0.85, 0.68, 0.03, COLORS.glass, 0.47, 1.69, -HALF_L + 0.06);
  body.box(0.09, 0.74, 0.09, COLORS.cream, 0, 1.69, -HALF_L + 0.06);
  body.box(BODY_W - WALL, WINDOW_TOP_BAND - WINDOW_TOP, WALL, COLORS.cream, 0, (WINDOW_TOP + WINDOW_TOP_BAND) / 2, -HALF_L + WALL / 2);
  body.box(BODY_W - WALL, 0.05, WALL, COLORS.maroon, 0, WINDOW_TOP_BAND + 0.025, -HALF_L + WALL / 2);

  // Wipers parked across the glass.
  for (const side of [-1, 1]) {
    body.box(0.6, 0.03, 0.04, COLORS.dash, side * 0.33, 1.4, -HALF_L + 0.09);
  }

  // Destination board behind the glass, in the corner where every driver
  // sticks one.
  body.box(0.6, 0.14, 0.02, 0xf0ead9, 0.4, 1.97, -HALF_L + 0.1);
  body.box(0.54, 0.035, 0.01, COLORS.maroon, 0.4, 1.95, -HALF_L + 0.12);

  // Grille in the blue, then the bumper with its lamp cluster.
  body.box(0.56, 0.16, 0.05, COLORS.dash, 0, 0.9, -HALF_L - 0.01);
  body.box(BODY_W + 0.02, 0.26, 0.22, COLORS.dash, 0, 0.52, -HALF_L - 0.05);
  for (const side of [-1, 1]) {
    lamps.box(0.3, 0.15, 0.05, 0xffe9bd, side * 0.62, 0.54, -HALF_L - 0.16);
    lamps.box(0.11, 0.11, 0.05, 0xd88a2a, side * 0.87, 0.54, -HALF_L - 0.16);
  }

  // Mirrors on stalks, angled back past the screen.
  for (const side of [-1, 1]) {
    body.beam(side * 0.96, 2.0, -HALF_L + 0.06, side * 1.2, 1.86, -HALF_L - 0.06, 0.022, COLORS.steel);
    body.box(0.05, 0.26, 0.17, COLORS.dash, side * 1.21, 1.86, -HALF_L - 0.06);
  }

  /* ---- Rear ----------------------------------------------------------- */

  body.box(BODY_W - WALL, 0.9, WALL, COLORS.cream, 0, 0.91, HALF_L - WALL / 2);
  glass.box(1.5, 0.64, 0.03, COLORS.glass, 0, 1.68, HALF_L - 0.04);
  body.box(BODY_W - WALL, 0.22, WALL, COLORS.cream, 0, 2.11, HALF_L - WALL / 2);
  body.box(BODY_W - WALL, 0.05, WALL, COLORS.maroon, 0, WINDOW_TOP_BAND + 0.025, HALF_L - WALL / 2);
  body.box(BODY_W + 0.02, 0.26, 0.2, COLORS.dash, 0, 0.52, HALF_L + 0.05);
  for (const side of [-1, 1]) {
    lamps.box(0.2, 0.28, 0.05, 0xb8342a, side * 0.74, 0.88, HALF_L + 0.01);
    lamps.box(0.13, 0.1, 0.05, 0xd88a2a, side * 0.74, 1.14, HALF_L + 0.01);
  }

  /* ---- Roof and luggage rack ------------------------------------------ */

  body.box(BODY_W - 0.1, 0.16, HALF_L * 2 - 0.12, COLORS.cream, 0, ROOF_TOP - 0.08, 0);
  body.box(BODY_W - 0.24, 0.1, HALF_L * 2 - 0.5, COLORS.creamShade, 0, ROOF_TOP + 0.05, 0);
  body.box(0.66, 0.1, 0.86, COLORS.creamShade, 0, ROOF_TOP + 0.06, -1.2);

  for (const side of [-1, 1]) {
    body.box(0.05, 0.05, 6.4, COLORS.chrome, side * 0.8, ROOF_TOP + 0.16, 0.05);
    for (let i = 0; i < 5; i += 1) {
      const z = -2.9 + i * 1.45;
      body.box(0.05, 0.14, 0.05, COLORS.chrome, side * 0.8, ROOF_TOP + 0.09, z);
    }
  }
  for (const z of [-2.3, 0, 2.3]) {
    body.box(1.62, 0.04, 0.04, COLORS.chrome, 0, ROOF_TOP + 0.16, z);
  }

  /* ---- Doorway -------------------------------------------------------- */

  const doorZ = (DOOR_Z0 + DOOR_Z1) / 2;
  const doorWidth = DOOR_Z1 - DOOR_Z0 - 0.06;
  // Frame posts and the step a passenger lands on.
  body.box(0.05, WINDOW_TOP - SKIRT_BOTTOM, 0.05, COLORS.steel, 0.99, (SKIRT_BOTTOM + WINDOW_TOP) / 2, DOOR_Z0 + 0.02);
  body.box(0.05, WINDOW_TOP - SKIRT_BOTTOM, 0.05, COLORS.steel, 0.99, (SKIRT_BOTTOM + WINDOW_TOP) / 2, DOOR_Z1 - 0.02);
  body.box(0.42, 0.07, doorWidth, COLORS.interior, 0.8, 0.43, doorZ);
  body.box(0.05, 0.05, doorWidth, COLORS.steel, 0.97, 1.02, doorZ);

  /* ---- Cabin ---------------------------------------------------------- */

  body.box(1.76, 0.08, 6.5, COLORS.interior, 0, FLOOR_TOP - 0.04, 0);
  // Bulkhead behind the driver and the dash rail.
  body.box(1.76, 0.22, 0.5, COLORS.dash, 0, 1.16, -3.0);

  for (const seat of BUS_SEATS) {
    const rowZ = seat.z;
    body.box(0.44, 0.1, 0.46, COLORS.seat, seat.x, 1.07, rowZ);
    body.box(0.44, 0.46, 0.08, COLORS.seat, seat.x, 1.3, rowZ + 0.24);
  }

  // Driver: left-hand side, hands on a tilted wheel.
  body.box(0.42, 0.5, 0.28, 0x6d7566, -0.56, 1.02, -2.44);
  body.box(0.4, 0.44, 0.1, 0x6d7566, -0.56, 1.28, -2.28);
  body.sphere(0.12, 8, 6, 0x5d3f2b, -0.56, 1.42, -2.44);
  body.cyl(0.115, 0.115, 0.07, 8, 0xe6e2d6, -0.56, 1.53, -2.44);
  body.cyl(0.17, 0.17, 0.03, 12, 0x2a2724, -0.56, 1.16, -2.76, { x: 1.05 });

  /* ---- Kumsary -------------------------------------------------------- */

  addKumsary(body);

  /* ---- Weathering ----------------------------------------------------- */

  // Rust runs downhill from seams and around the arches, so the patches are
  // short horizontal streaks rather than scattered blobs.
  for (let i = 0; i < 22; i += 1) {
    const side = i % 2 === 0 ? -1 : 1;
    const z = random.range(-3.1, 3.1);
    const y = random.range(0.5, 2.16);
    // Nothing weathers the open doorway.
    if (side > 0 && z > DOOR_Z0 - 0.2 && z < DOOR_Z1 + 0.2 && y < WINDOW_TOP) continue;
    const length = random.range(0.18, 0.62);
    body.box(
      0.008,
      random.range(0.018, 0.055),
      length,
      i % 3 === 0 ? COLORS.rustDark : COLORS.rust,
      side * (HALF_W + 0.001),
      y,
      z,
    );
  }

  /* ---- Meshes --------------------------------------------------------- */

  const bodyMesh = new Mesh(
    body.merge(),
    new MeshStandardMaterial({ vertexColors: true, roughness: 0.72, metalness: 0.1 }),
  );
  bodyMesh.castShadow = true;
  bodyMesh.receiveShadow = true;
  bodyMesh.name = 'bus-body';
  group.add(bodyMesh);

  const glassMesh = new Mesh(
    glass.merge(),
    new MeshStandardMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.34,
      roughness: 0.16,
      metalness: 0.2,
      depthWrite: false,
    }),
  );
  glassMesh.name = 'bus-glass';
  group.add(glassMesh);

  const lampMesh = new Mesh(
    lamps.merge(),
    new MeshBasicMaterial({ vertexColors: true, toneMapped: false }),
  );
  lampMesh.name = 'bus-lamps';
  group.add(lampMesh);

  group.add(buildKumsaryArm());
  return group;
}

/**
 * The kumsary stands in the doorway facing the street: left hand up on the
 * front door post, right arm out over the road hailing fares. Only that arm
 * moves, so it is a separate mesh on a pivot at the shoulder.
 */
function addKumsary(parts) {
  const { skin, shirt, trousers, cap } = COLORS;
  const x = 0.78;
  const z = (DOOR_Z0 + DOOR_Z1) / 2;

  // Legs from the step up to the hips, feet planted apart on the tread.
  parts.beam(x - 0.01, 0.46, z - 0.13, x + 0.04, 1.02, z - 0.11, 0.07, trousers);
  parts.beam(x - 0.01, 0.46, z + 0.13, x + 0.04, 1.02, z + 0.11, 0.07, trousers);
  parts.box(0.2, 0.07, 0.12, 0x24201c, x + 0.02, 0.47, z - 0.13);
  parts.box(0.2, 0.07, 0.12, 0x24201c, x + 0.02, 0.47, z + 0.13);

  // Torso, stepping out over the road.
  parts.box(0.26, 0.3, 0.38, shirt, x + 0.04, 1.16, z);
  parts.box(0.26, 0.28, 0.4, shirt, x + 0.1, 1.44, z);
  parts.box(0.24, 0.12, 0.42, shirt, x + 0.13, 1.57, z);

  // Head and cap.
  parts.sphere(0.115, 8, 6, skin, x + 0.17, 1.72, z);
  parts.cyl(0.12, 0.12, 0.07, 10, cap, x + 0.17, 1.81, z);

  // Braced arm: shoulder to the front door post — the hand that keeps him in
  // the doorway while the rest of him leans out.
  parts.beam(x + 0.13, 1.57, z - 0.19, 0.96, 1.9, DOOR_Z0 + 0.09, 0.05, shirt);
  parts.box(0.09, 0.08, 0.09, skin, 0.96, 1.9, DOOR_Z0 + 0.09);
}

/** The hailing arm, authored hanging straight down so one Z rotation swings it
 *  out over the road. Sleeve, bare forearm, hand — same as the figure in the
 *  reference. */
function buildKumsaryArm() {
  const pivot = new Group();
  pivot.name = 'bus-kumsary-arm';
  pivot.position.set(0.9, 1.54, (DOOR_Z0 + DOOR_Z1) / 2 + 0.2);

  const parts = new Parts();
  parts.beam(0, 0, 0, 0.02, -0.16, 0.02, 0.05, COLORS.shirt);
  parts.beam(0.02, -0.16, 0.02, 0.06, -0.3, 0.06, 0.042, COLORS.skin);
  parts.sphere(0.055, 6, 5, COLORS.skin, 0.07, -0.33, 0.07);

  const mesh = new Mesh(
    parts.merge(),
    new MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0.02 }),
  );
  mesh.castShadow = true;
  mesh.name = 'bus-kumsary-hand';
  pivot.add(mesh);
  pivot.rotation.z = 1.75;
  return pivot;
}

/* ------------------------------------------------------------------ */
/* Routes                                                              */
/* ------------------------------------------------------------------ */

/**
 * One closed circuit per bus. The legs run at the kerb, blend inboard past the
 * fuel queues (where the outer lane is already taken by waiting cars) and turn
 * through the median openings at each end.
 */
export function createBusFleet({ route, random, makeCar, queueSpans, config }) {
  const settings = { ...BUS_DEFAULTS, ...(config ?? {}) };
  const buses = [];
  const laneAt = makeLaneProfile(queueSpans);

  BUS_SPANS.forEach(([fromIndex, toIndex], index) => {
    const from = MEDIAN_OPENINGS[fromIndex].s;
    const to = MEDIAN_OPENINGS[toIndex].s;
    if (!(to > from)) return;

    const curve = buildBusCurve(route, from, to, laneAt);
    const length = curve.getLength();
    const speedProfile = buildBusSpeedProfile(curve, length, settings);

    // Spread the fleet around the street so one is never far away.
    const startDistance = (length * (index + 0.35)) / BUS_SPANS.length;
    const point = curve.getPointAt(startDistance / length);
    const tangent = curve.getTangentAt(startDistance / length);

    const record = makeCar(point.x, point.z, Math.atan2(-tangent.x, -tangent.z), {
      type: 'bus',
      hazard: false,
      scale: 1,
      color: 0xe4dac6,
    });

    record.state = 'returning';
    record.rideType = 'bus';
    // `raksha` is the shared ride-state bag: passengers aboard, the stop the
    // vehicle is holding for, and the timers that govern both.
    record.rakshaConfig = settings;
    record.raksha = {
      passengers: [],
      stop: null,
      target: null,
      stopTimer: 0,
      cooldown: random.range(0, 3),
      dropStarted: false,
    };
    // The ride manager reads these two instead of a queue slot.
    record.exitLength = length;
    record.exitDistance = startDistance;
    record.exitSpeed = 6;
    // Buses run on rails: military shoves and rage corrections must not knock
    // them off the circuit.
    record.railed = true;

    buses.push({
      record,
      curve,
      length,
      speedProfile,
      from,
      to,
      startDistance,
    });
  });

  return {
    buses,
    update(dt, panic, hitchhikers) {
      for (const bus of buses) updateBus(bus, dt, panic, hitchhikers);
    },
  };
}

/** Lateral offset for a given s on the northbound leg: at the kerb, but pulled
 *  inboard wherever a queue is parked in the outer lane. */
function makeLaneProfile(queueSpans) {
  const spans = queueSpans ?? [];
  return (s, side) => {
    if (side < 0 || spans.length === 0) return BUS_LANE;
    let closeness = 0;
    for (const span of spans) {
      const gap = s < span.start ? span.start - s : s > span.end ? s - span.end : 0;
      closeness = Math.max(closeness, 1 - smoothstep(gap / LANE_BLEND));
    }
    return BUS_LANE + (QUEUE_LANE - BUS_LANE) * closeness;
  };
}

function smoothstep(t) {
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
}

function buildBusCurve(route, from, to, laneAt) {
  const points = [];
  // The first sample of every leg is the last sample of the segment before it,
  // so legs start one step in and the curve never carries a duplicate point.
  const leg = (fromS, toS, side) => {
    const span = Math.abs(toS - fromS);
    const steps = Math.max(2, Math.ceil(span / 30));
    for (let i = 1; i <= steps; i += 1) {
      const s = fromS + (toS - fromS) * (i / steps);
      points.push(route.offsetFrame(s, side * laneAt(s, side)).position);
    }
  };
  const uTurn = (turnS, dir, fromSide, closes) => {
    // Out to the centre line and back, with the apex a lane-width past the
    // turn point so the arc stays inside the median gap.
    const lane = laneAt(turnS, 1);
    const apex = lane * 0.707;
    points.push(
      route.offsetFrame(turnS + dir * apex, fromSide * apex).position,
      route.offsetFrame(turnS + dir * lane, 0).position,
      route.offsetFrame(turnS + dir * apex, -fromSide * apex).position,
    );
    // The closing turn ends where the first leg began; the closed spline draws
    // that last arc itself.
    if (!closes) points.push(route.offsetFrame(turnS, -fromSide * lane).position);
  };

  leg(from, to, 1);
  uTurn(to, 1, 1, false);
  leg(to, from, -1);
  uTurn(from, -1, -1, true);

  return new CatmullRomCurve3(points, true, 'centripetal', 0.5);
}

function buildBusSpeedProfile(curve, length, settings) {
  const count = Math.max(120, Math.min(900, Math.ceil(length / 2)));
  const step = length / count;
  const limits = new Float32Array(count + 1);

  let previous = curve.getTangentAt(0);
  for (let i = 1; i <= count; i += 1) {
    const tangent = curve.getTangentAt(i / count);
    const curvature = previous.angleTo(tangent) / step;
    const limit = curvature > 1e-5
      ? Math.sqrt(3.2 / curvature)
      : settings.topSpeed;
    limits[i] = Math.max(settings.minSpeed, Math.min(settings.topSpeed, limit));
    previous = tangent;
  }
  limits[0] = limits[1];

  for (let pass = 0; pass < 2; pass += 1) {
    const copy = limits.slice();
    for (let i = 1; i < count; i += 1) {
      limits[i] = (copy[i - 1] + 2 * copy[i] + copy[i + 1]) * 0.25;
    }
  }

  // Buses brake for the U-turn rather than arriving at it.
  for (let i = count - 1; i >= 0; i -= 1) {
    limits[i] = Math.min(limits[i], Math.sqrt(limits[i + 1] ** 2 + 2 * 2.2 * step));
  }

  return { count, step, limits };
}

function sampleProfile(profile, u) {
  const x = Math.max(0, Math.min(1, u)) * profile.count;
  const index = Math.min(profile.count - 1, Math.floor(x));
  const fraction = x - index;
  return profile.limits[index] * (1 - fraction) + profile.limits[index + 1] * fraction;
}

function updateBus(bus, dt, panic, hitchhikers) {
  const { record, curve, length, speedProfile } = bus;
  // The ride manager only needs exitLength/exitDistance/exitSpeed and the ride
  // state bag, so a bus drives the same pickup logic as a raksha.
  const control = hitchhikers?.beforeRideMove(record, dt) ?? null;

  let target = sampleProfile(speedProfile, record.exitDistance / length) * panic;
  if (control?.speedScale !== undefined) target *= control.speedScale;
  if (control?.hold) target = 0;

  if (record.exitSpeed < target) {
    record.exitSpeed = Math.min(target, record.exitSpeed + 3.0 * dt);
  } else {
    record.exitSpeed = Math.max(target, record.exitSpeed - 5.2 * dt);
  }

  const previous = record.exitDistance;
  record.exitDistance += record.exitSpeed * dt;
  if (record.exitDistance >= length) record.exitDistance -= length;

  const u = record.exitDistance / length;
  const point = curve.getPointAt(u);
  const tangent = curve.getTangentAt(u);
  record.x = point.x;
  record.z = point.z;
  record.y = 0.02;
  record.yaw = Math.atan2(-tangent.x, -tangent.z);

  // A wrapped lap reads as a backwards jump; the manager ignores it.
  hitchhikers?.afterRideMove(record, record.exitDistance - previous);
}
